import { useState, useEffect, useCallback } from 'react';
import { meetingNotesApi } from '../lib/api';

export default function MarketIntelligence() {
  const [insights, setInsights] = useState([]);
  const [quota, setQuota] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [loading, setLoading] = useState(true);

  const loadData = useCallback(async () => {
    try {
      const [insightsRes, statusRes] = await Promise.all([
        meetingNotesApi.getInsights(),
        meetingNotesApi.getStatus(),
      ]);
      setInsights(insightsRes.insights || []);
      setQuota(statusRes);
    } catch (err) {
      console.error('Load market intelligence error:', err);
    }
    setLoading(false);
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const handleAnalyze = async () => {
    setAnalyzing(true);
    try {
      await meetingNotesApi.runAnalysis();
      await loadData();
    } catch (err) {
      alert('Analysis failed: ' + err.message);
    }
    setAnalyzing(false);
  };

  const timingColor = (score) => {
    if (score > 70) return 'text-emerald-600';
    if (score >= 40) return 'text-amber-600';
    return 'text-gray-400';
  };

  const parseSummaryBullets = (summary) => {
    if (!summary) return [];
    return summary.split('\n').map(s => s.replace(/^[-•*]\s*/, '').trim()).filter(Boolean);
  };

  // Compute stats
  const pitchWindowCount = insights.filter(i => i.overlaps?.some(o => o.pitch_window)).length;
  const avgTiming = insights.length > 0
    ? Math.round(insights.reduce((sum, i) => sum + (i.timing_score || 0), 0) / insights.length)
    : 0;

  // Group insights by company
  const byCompany = {};
  insights.forEach(insight => {
    const name = insight.meeting_notes?.companies?.name || 'Unknown';
    if (!byCompany[name]) byCompany[name] = [];
    byCompany[name].push(insight);
  });
  const companyNames = Object.keys(byCompany).sort();

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <p className="text-sm text-gray-400">Loading intelligence...</p>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-start justify-between mb-8">
        <div>
          <h1 className="font-heading text-3xl text-vault-navy">Market Intelligence</h1>
          <p className="text-sm text-gray-400 mt-1">AI-powered analysis of your meeting notes against your wishlist and market trends.</p>
        </div>
        <div className="flex flex-col items-end gap-1 shrink-0">
          <button
            onClick={handleAnalyze}
            disabled={analyzing || (quota && !quota.can_run)}
            className="px-5 py-2.5 rounded-btn text-sm font-semibold text-white disabled:opacity-50 flex items-center gap-2"
            style={{ background: 'linear-gradient(135deg, #c9a84c, #F59E0B)' }}
          >
            {analyzing && (
              <span className="w-2 h-2 rounded-full bg-white/70 animate-pulse" />
            )}
            {analyzing ? 'Analyzing...' : 'Run Analysis'}
          </button>
          {quota && (
            <span className="text-xs text-gray-400">
              {quota.can_run
                ? `${quota.used} of ${quota.limit} runs used this month`
                : `Resets ${quota.reset_date}`}
            </span>
          )}
        </div>
      </div>

      {/* Stats row */}
      {insights.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <div className="glass p-5">
            <div className="text-[12px] font-semibold text-gray-400 uppercase tracking-wider mb-1">Total Insights</div>
            <div className="font-heading text-4xl text-vault-navy">{insights.length}</div>
            <div className="text-xs text-gray-400 mt-1">Across all companies</div>
          </div>
          <div className="glass p-5 border-2 border-amber-300/40 bg-amber-50/20">
            <div className="text-[12px] font-semibold text-amber-700 uppercase tracking-wider mb-1">Pitch Windows Active</div>
            <div className="font-heading text-4xl text-amber-700">{pitchWindowCount}</div>
            <div className="text-xs text-gray-400 mt-1">High-priority opportunities</div>
          </div>
          <div className="glass p-5">
            <div className="text-[12px] font-semibold text-gray-400 uppercase tracking-wider mb-1">Avg Timing Score</div>
            <div className={`font-heading text-4xl ${timingColor(avgTiming)}`}>{avgTiming}</div>
            <div className="text-xs text-gray-400 mt-1">Out of 100</div>
          </div>
        </div>
      )}

      {/* Empty state */}
      {insights.length === 0 ? (
        <div className="glass p-12 text-center">
          <div className="text-4xl mb-4">🧠</div>
          <h2 className="font-heading text-2xl text-vault-navy mb-3">No intelligence yet</h2>
          <p className="text-sm text-gray-400 mb-6 max-w-md mx-auto">
            Run your first analysis to cross-reference your meeting notes against your wishlist and market trends.
          </p>
          <button
            onClick={handleAnalyze}
            disabled={analyzing || (quota && !quota.can_run)}
            className="px-6 py-3 rounded-btn text-sm font-semibold text-white disabled:opacity-50 inline-flex items-center gap-2"
            style={{ background: 'linear-gradient(135deg, #c9a84c, #F59E0B)' }}
          >
            {analyzing ? 'Analyzing...' : 'Run Analysis'}
          </button>
        </div>
      ) : (
        /* Insights grouped by company */
        <div className="space-y-8">
          {companyNames.map(companyName => (
            <div key={companyName}>
              {/* Company section header */}
              <div className="glass p-4 mb-4 flex items-center gap-3 border-l-4 border-vault-gold">
                <h2 className="font-heading text-xl text-vault-navy">{companyName}</h2>
                <span className="text-xs text-gray-400">{byCompany[companyName].length} insight{byCompany[companyName].length !== 1 ? 's' : ''}</span>
              </div>

              <div className="space-y-4 pl-2">
                {byCompany[companyName].map(insight => {
                  const hasPitchWindow = insight.overlaps?.some(o => o.pitch_window === true);
                  const bullets = parseSummaryBullets(insight.summary);
                  const meetingDate = insight.meeting_notes?.meeting_date
                    ? new Date(insight.meeting_notes.meeting_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                    : null;
                  const notePreview = insight.meeting_notes?.note?.slice(0, 100) + (insight.meeting_notes?.note?.length > 100 ? '...' : '');

                  return (
                    <div key={insight.id} className="glass p-6">
                      {/* Meeting date + note preview */}
                      <div className="flex items-center gap-2 mb-3">
                        {meetingDate && (
                          <span className="text-xs font-semibold text-vault-gold">{meetingDate}</span>
                        )}
                        {notePreview && (
                          <span className="text-xs text-gray-400 truncate">{notePreview}</span>
                        )}
                      </div>

                      {/* Pitch window banner */}
                      {hasPitchWindow && (
                        <div className="mb-4 px-4 py-3 rounded-lg border-2 border-amber-400/50 bg-amber-50/50">
                          <p className="font-semibold text-amber-700 text-sm">Pitch Window Open</p>
                          {insight.overlaps?.filter(o => o.pitch_window).map((o, i) => (
                            <p key={i} className="text-xs text-amber-600 mt-0.5">
                              {o.signal || o.category || 'Signal detected'}
                            </p>
                          ))}
                        </div>
                      )}

                      {/* Summary bullets */}
                      {bullets.length > 0 && (
                        <ul className="space-y-1 mb-4">
                          {bullets.map((b, i) => (
                            <li key={i} className="text-sm text-gray-600 flex items-start gap-2">
                              <span className="text-gray-300 shrink-0 mt-0.5">&bull;</span>
                              <span>{b}</span>
                            </li>
                          ))}
                        </ul>
                      )}

                      {/* Wishlist match chips (company format: signal + category) */}
                      {insight.overlaps?.length > 0 && (
                        <div className="mb-3">
                          <div className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-1.5">Wishlist Matches</div>
                          <div className="flex flex-wrap gap-1.5">
                            {insight.overlaps.map((o, i) => (
                              <span
                                key={i}
                                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-indigo-50 text-indigo-700"
                              >
                                {o.signal && <span className="font-semibold">{o.signal}</span>}
                                {o.signal && o.category && <span className="text-indigo-400">/</span>}
                                {o.category && <span>{o.category}</span>}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Trend signal badges */}
                      {insight.overlaps?.some(o => o.category) && (
                        <div className="flex flex-wrap gap-1 mb-3">
                          {[...new Set(insight.overlaps.filter(o => o.category).map(o => o.category))].map((cat, i) => (
                            <span
                              key={i}
                              className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-50 text-emerald-700"
                            >
                              {cat}
                            </span>
                          ))}
                        </div>
                      )}

                      {/* Next step */}
                      {insight.next_step && (
                        <p className="text-sm text-vault-gold font-medium mt-3">
                          &rarr; {insight.next_step}
                        </p>
                      )}

                      {/* Timing score */}
                      {typeof insight.timing_score === 'number' && (
                        <p className={`text-xs font-medium mt-2 ${timingColor(insight.timing_score)}`}>
                          Timing score: {insight.timing_score}/100
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
